import {
  Lock as LockIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  RestartAlt as RefreshIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { auth } from '../firebase';
import { clientApi } from '../services/clientApi';
import { colors } from '../theme';

const ClientSettingsPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);

  // Profil Bilgileri
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [age, setAge] = useState('');
  const [goal, setGoal] = useState('');

  // Şifre Değiştirme
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Bildirim Tercihleri
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [programReminders, setProgramReminders] = useState(true);
  const [weightReminders, setWeightReminders] = useState(true);
  const [messageNotifications, setMessageNotifications] = useState(true);

  useEffect(() => {
    loadClientData();
  }, []);

  const loadClientData = async () => {
    try {
      const userEmail = localStorage.getItem('userEmail') || '';
      setEmail(userEmail);

      const client = await clientApi.getClientByEmail(userEmail);
      setFirstName(client.firstName);
      setLastName(client.lastName);
      setPhone(client.phone || '');
      setAge(client.age?.toString() || '');
      setGoal(client.goal || '');
    } catch (error) {
      console.error('Error loading client data:', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      const userEmail = localStorage.getItem('userEmail') || '';
      const client = await clientApi.getClientByEmail(userEmail);

      await clientApi.updateClient(client.id, {
        firstName,
        lastName,
        email: client.email,
        phone,
        age: age ? parseInt(age) : undefined,
        goal,
      });

      showSuccess('Profil bilgileriniz başarıyla güncellendi!', 'Başarılı');
    } catch (error) {
      showError('Profil güncellenirken bir hata oluştu.', 'Güncelleme Hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      showError('Yeni şifreler eşleşmiyor!', 'Şifre Hatası');
      return;
    }

    if (newPassword.length < 6) {
      showError('Yeni şifre en az 6 karakter olmalıdır!', 'Şifre Hatası');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;

      if (user && user.email) {
        const { EmailAuthProvider, reauthenticateWithCredential } =
          await import('firebase/auth');
        const credential = EmailAuthProvider.credential(
          user.email,
          currentPassword
        );
        await reauthenticateWithCredential(user, credential);

        const { updatePassword } = await import('firebase/auth');
        await updatePassword(user, newPassword);

        showSuccess('Şifreniz başarıyla değiştirildi!', 'Başarılı');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        showError('Mevcut şifreniz yanlış!', 'Şifre Hatası');
      } else {
        showError(
          'Şifre değiştirilirken bir hata oluştu.',
          'Güncelleme Hatası'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = () => {
    showSuccess('Bildirim tercihleri kaydedildi!', 'Başarılı');
  };

  const handleResetNotifications = () => {
    setEmailNotifications(true);
    setProgramReminders(true);
    setWeightReminders(true);
    setMessageNotifications(true);
    showSuccess('Bildirim tercihleri varsayılana döndürüldü!', 'Sıfırlandı');
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Başlık */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SettingsIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: colors.text.primary }}
            >
              Ayarlar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hesap ve tercih ayarlarınızı yönetin
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Profil Bilgileri */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card
            sx={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <PersonIcon sx={{ color: colors.primary.main, fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  Profil Bilgileri
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Box display="flex" flexDirection="column" gap={2.5}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Ad"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Soyad"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Email"
                  value={email}
                  disabled
                  variant="outlined"
                  helperText="Email adresi değiştirilemez"
                />

                <TextField
                  fullWidth
                  label="Telefon"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  variant="outlined"
                  placeholder="0555 123 45 67"
                />

                <TextField
                  fullWidth
                  label="Yaş"
                  value={age}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      setAge(value);
                    }
                  }}
                  variant="outlined"
                  type="number"
                  placeholder="25"
                />

                <TextField
                  fullWidth
                  label="Hedef"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  variant="outlined"
                  multiline
                  rows={3}
                  placeholder="Örn: Kilo vermek, Kas yapmak, Sağlıklı beslenme"
                />

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveProfile}
                  disabled={loading}
                  sx={{
                    bgcolor: colors.primary.main,
                    color: 'white',
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: colors.hover.primary,
                    },
                  }}
                >
                  Profili Kaydet
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Şifre Değiştirme */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card
            sx={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <LockIcon sx={{ color: colors.primary.main, fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  Şifre Değiştir
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Box display="flex" flexDirection="column" gap={2.5}>
                <Alert severity="info" sx={{ fontSize: '0.9rem' }}>
                  Güvenliğiniz için düzenli olarak şifrenizi değiştirmenizi
                  öneririz.
                </Alert>

                <TextField
                  fullWidth
                  type="password"
                  label="Mevcut Şifre"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  type="password"
                  label="Yeni Şifre"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  variant="outlined"
                  helperText="En az 6 karakter"
                />

                <TextField
                  fullWidth
                  type="password"
                  label="Yeni Şifre (Tekrar)"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  variant="outlined"
                />

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleChangePassword}
                  disabled={
                    loading ||
                    !currentPassword ||
                    !newPassword ||
                    !confirmPassword
                  }
                  sx={{
                    bgcolor: colors.primary.main,
                    color: 'white',
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: colors.hover.primary,
                    },
                  }}
                >
                  Şifreyi Güncelle
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Bildirim Tercihleri */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <NotificationsIcon
                  sx={{ color: colors.primary.main, fontSize: 28 }}
                />
                <Typography variant="h6" fontWeight={600}>
                  Bildirim Tercihleri
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: colors.background.light,
                      borderRadius: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailNotifications}
                          onChange={(e) =>
                            setEmailNotifications(e.target.checked)
                          }
                          color="success"
                        />
                      }
                      label={
                        <Box>
                          <Typography fontWeight={600} fontSize="1rem">
                            Email Bildirimleri
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Genel bilgilendirme emaillerini al
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: colors.background.light,
                      borderRadius: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={programReminders}
                          onChange={(e) =>
                            setProgramReminders(e.target.checked)
                          }
                          color="success"
                        />
                      }
                      label={
                        <Box>
                          <Typography fontWeight={600} fontSize="1rem">
                            Program Hatırlatmaları
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Öğün ve program hatırlatmaları al
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: colors.background.light,
                      borderRadius: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={weightReminders}
                          onChange={(e) => setWeightReminders(e.target.checked)}
                          color="success"
                        />
                      }
                      label={
                        <Box>
                          <Typography fontWeight={600} fontSize="1rem">
                            Tartı Hatırlatmaları
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Haftalık kilo ölçümü hatırlatmaları
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: colors.background.light,
                      borderRadius: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={messageNotifications}
                          onChange={(e) =>
                            setMessageNotifications(e.target.checked)
                          }
                          color="success"
                        />
                      }
                      label={
                        <Box>
                          <Typography fontWeight={600} fontSize="1rem">
                            Mesaj Bildirimleri
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Diyetisyeninizden mesaj geldiğinde bildirim al
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>
              </Grid>

              <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleResetNotifications}
                  sx={{
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    py: 1,
                    px: 2.5,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    width: '150px',
                    '&:hover': {
                      borderColor: colors.hover.primary,
                      bgcolor: 'rgba(46, 125, 50, 0.04)',
                    },
                  }}
                >
                  Sıfırla
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveNotifications}
                  sx={{
                    bgcolor: colors.primary.main,
                    color: 'white',
                    py: 1,
                    px: 2.5,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    width: '150px',
                    boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
                    '&:hover': {
                      bgcolor: colors.hover.primary,
                      boxShadow: '0 4px 12px rgba(46, 125, 50, 0.4)',
                    },
                  }}
                >
                  Kaydet
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientSettingsPage;
